import { useCallback } from 'react';
import { useFormikContext } from 'formik';

import { EMPTY_ARRAY, EMPTY_STRING } from '@e-socna/empty';

import { DraggableList } from '../../../../components/DraggableList';
import { Loading } from '../../../../components/Loading';
import { useFindAllMaterialsQuery } from '../../../../components/MaterialList/hooks/useFindAllMaterialsQuery.hook';
import { useContextualizedFields } from '../../../../hooks/useContextualizedFields.hook';

import type {
  OptionalMaterialUsageFormContext,
  RequiredMaterialUsageFormContext,
  MaterialUsageForm_MaterialUsage,
} from '../../fragments/material-usage.fragment';

interface Props {
  readonly formContext?: string;
  readonly id: string;
}

export const MaterialsOrderFormStep = ({ formContext }: Readonly<Props>) => {
  const { setFieldValue } = useFormikContext<
    OptionalMaterialUsageFormContext | RequiredMaterialUsageFormContext
  >();

  const fields = useContextualizedFields<
    Readonly<{
      materialUsages: readonly MaterialUsageForm_MaterialUsage[] | undefined;
    }>
  >(
    {
      materialUsages: {
        id: 'material-usages-order',
        name: 'materialUsages',
      },
    },
    formContext,
  );

  const setItems = useCallback(
    (newOrderedListOfMaterials: readonly MaterialUsageForm_MaterialUsage[]) => {
      void setFieldValue(fields.materialUsages.name, newOrderedListOfMaterials);
    },
    [setFieldValue, fields.materialUsages.name],
  );

  const { data, isLoading } = useFindAllMaterialsQuery();

  const getLabel = useCallback(
    (materialUsage: Readonly<MaterialUsageForm_MaterialUsage>) => {
      const allMaterials = data?.findAllMaterials ?? EMPTY_ARRAY;

      const resolvedMaterialUsage = allMaterials.find(
        (material) => material._id === materialUsage.materialId,
      );

      return resolvedMaterialUsage
        ? `${resolvedMaterialUsage.code} - ${resolvedMaterialUsage.label}`
        : EMPTY_STRING;
    },
    [data?.findAllMaterials],
  );

  if (isLoading) {
    return <Loading />;
  }

  return (
    <DraggableList
      getLabel={getLabel}
      idProp="materialId"
      indexProp="index"
      items={fields.materialUsages.value ?? EMPTY_ARRAY}
      setItems={setItems}
    />
  );
};
